import React, { useState, useEffect, useRef } from 'react';
import { WifiOff, Loader2, CheckCircle2, CloudOff, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { flushQueue, getQueueCount } from '../lib/offlineQueue';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Top-of-page connectivity banner + offline sync indicator.
 * - Polls `/api/health` every 10s
 * - Shows offline/retrying/recovered banners
 * - Displays pending offline queue count + auto-flushes when back online
 */
export default function ConnectivityBanner() {
  const [status, setStatus] = useState('ok'); // 'ok' | 'retrying' | 'offline' | 'recovered'
  const [pendingCount, setPendingCount] = useState(getQueueCount());
  const [syncing, setSyncing] = useState(false);
  const failures = useRef(0);
  const recoverTimer = useRef(null);
  const wasOffline = useRef(false);

  const refreshPending = () => setPendingCount(getQueueCount());

  const runSync = async () => {
    if (syncing) return;
    if (getQueueCount() === 0) return;
    setSyncing(true);
    try {
      await flushQueue(api);
    } finally {
      setSyncing(false);
      refreshPending();
    }
  };

  const check = async () => {
    try {
      // Hard 5-second cap on the health probe so it never piles up while a
      // long-running AI call holds the (single) backend worker. Without this,
      // a 30s AI extraction would fire 3+ healthcheck timeouts and flip us
      // to "offline" even though the worker is just busy.
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 5000);
      const res = await fetch(`${API_URL}/api/health`, {
        method: 'GET', cache: 'no-store', signal: ctrl.signal,
      });
      clearTimeout(t);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const wasDown = failures.current > 0 || wasOffline.current;
      failures.current = 0;
      wasOffline.current = false;
      if (wasDown) {
        setStatus('recovered');
        clearTimeout(recoverTimer.current);
        recoverTimer.current = setTimeout(() => setStatus('ok'), 2500);
        runSync();
      } else {
        setStatus('ok');
      }
    } catch (_) {
      // If the user has an in-flight long AI request, suppress the offline
      // banner — the worker is just busy. Hot-reload + AI both set this flag.
      if (window.__savenesAiBusy) {
        setStatus('ok');
        return;
      }
      failures.current += 1;
      // Wider tolerance (was 3 → now 6 = ~60s) so a busy worker doesn't
      // get reported as offline.
      if (failures.current >= 6) {
        setStatus('offline');
        wasOffline.current = true;
      } else {
        setStatus('retrying');
      }
    }
  };

  useEffect(() => {
    check();
    const id = setInterval(check, 10000);
    const handleOffline = () => { failures.current = 3; wasOffline.current = true; setStatus('offline'); };
    const handleOnline = () => { check(); };
    const handleQueueChange = (e) => setPendingCount(e.detail?.count ?? getQueueCount());
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline-queue-changed', handleQueueChange);
    return () => {
      clearInterval(id);
      clearTimeout(recoverTimer.current);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline-queue-changed', handleQueueChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nothing to show
  if (status === 'ok' && pendingCount === 0) return null;

  // When online but pending items exist → small pill at top-right rather than full banner
  if (status === 'ok' && pendingCount > 0) {
    return (
      <div
        className="fixed top-2 right-2 z-[9999] bg-white border border-[#E2E8F0] shadow-md rounded-full px-3 py-1.5 flex items-center gap-2 text-xs"
        data-testid="offline-pending-pill"
      >
        <CloudOff className="w-3.5 h-3.5 text-[#CA8A04]" />
        <span className="text-[#0A2342] font-medium">{pendingCount} pending {pendingCount === 1 ? 'entry' : 'entries'}</span>
        <button
          type="button"
          onClick={runSync}
          disabled={syncing}
          className="text-[#0E7490] hover:text-[#0A2342] underline disabled:opacity-60"
          data-testid="offline-sync-now-btn"
        >
          {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Sync now</>}
        </button>
      </div>
    );
  }

  const cfg = {
    retrying: {
      bg: 'bg-[#FEF3C7]', border: 'border-[#FBBF24]', text: 'text-[#92400E]',
      icon: <Loader2 className="w-4 h-4 animate-spin" />,
      msg: 'Reconnecting to server…',
    },
    offline: {
      bg: 'bg-[#FEF2F2]', border: 'border-[#FECACA]', text: 'text-[#991B1B]',
      icon: <WifiOff className="w-4 h-4" />,
      msg: pendingCount > 0
        ? `Offline — ${pendingCount} ${pendingCount === 1 ? 'entry' : 'entries'} saved locally. Will sync when you reconnect.`
        : 'Offline — server is unreachable. Your work will be saved locally.',
    },
    recovered: {
      bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#166534]',
      icon: <CheckCircle2 className="w-4 h-4" />,
      msg: syncing ? 'Reconnected — syncing offline entries…' : 'Reconnected.',
    },
  }[status];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[9999] ${cfg.bg} border-b ${cfg.border} ${cfg.text} px-4 py-2 text-sm flex items-center justify-center gap-2 shadow-sm`}
      data-testid={`connectivity-banner-${status}`}
      role="status"
    >
      {cfg.icon}
      <span>{cfg.msg}</span>
      {status === 'offline' && (
        <button
          onClick={check}
          className="ml-3 underline hover:no-underline flex items-center gap-1"
          data-testid="connectivity-retry-btn"
        >
          <RefreshCw className="w-3 h-3" />Retry now
        </button>
      )}
    </div>
  );
}
